import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  HeartHandshake,
  Briefcase,
  Receipt,
  FileText,
  MapPin,
  Award,
  HandCoins,
  IdCard,
  PieChart,
  CalendarDays,
  Handshake,
  MessagesSquare,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { ASSOC, api } from "../lib/api";
import { Button } from "./ui/button";
import { Toaster } from "./ui/sonner";

const nav = [
  { to: "/app", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/app/membres", label: "Membres", icon: Users },
  { to: "/app/benevoles", label: "Bénévoles", icon: HeartHandshake },
  { to: "/app/salaries", label: "Salarié(e)s", icon: Briefcase },
  { to: "/app/partenaires", label: "Partenaires", icon: Handshake },
  { to: "/app/evenements", label: "Événements", icon: CalendarDays },
  { to: "/app/forum", label: "Forum", icon: MessagesSquare },
  { to: "/app/comptabilite", label: "Comptabilité", icon: Receipt },
  { to: "/app/bilan", label: "Bilan prév. / déf.", icon: PieChart },
  { type: "divider", label: "Documents" },
  { to: "/app/bulletins", label: "Bulletins de paie", icon: FileText },
  { to: "/app/ordres-mission", label: "Ordres de mission", icon: MapPin },
  { to: "/app/attestations", label: "Attestations bénévolat", icon: Award },
  { to: "/app/recus-don", label: "Reçus de don", icon: HandCoins },
  { to: "/app/cartes-membres", label: "Cartes de membre", icon: IdCard },
  { type: "divider", label: "Administration" },
  { to: "/app/parametres", label: "Paramètres", icon: Settings },
];

export default function Layout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me").catch(() => {
      navigate("/login");
    });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("aij_token");
    localStorage.removeItem("aij_user_label");
    navigate("/login");
  };

  const userLabel = localStorage.getItem("aij_user_label") || "Administrateur";

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-brand-navy text-white flex flex-col transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <img src={ASSOC.logo} alt="AIJ" className="h-11 w-11 rounded-full bg-white p-0.5 object-contain" />
          <div className="leading-tight">
            <div className="font-serif text-[17px] tracking-tight">Action Impact</div>
            <div className="font-serif text-[17px] tracking-tight -mt-1">Jeunesse</div>
            <div className="small-caps text-[10px] text-white/60 mt-1">Espace administration</div>
          </div>
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setOpen(false)}
            className="ml-auto lg:hidden text-white/70 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {nav.map((item, i) => {
            if (item.type === "divider") {
              return (
                <div key={i} className="px-3 pt-5 pb-2 small-caps text-white/50">
                  {item.label}
                </div>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/75 hover:bg-white/5 hover:text-white"
                  }`
                }
                onClick={() => setOpen(false)}
              >
                <Icon size={18} strokeWidth={1.6} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 mb-2 text-white/80">
            <div className="w-7 h-7 rounded-full bg-brand-orange/20 flex items-center justify-center text-xs font-semibold text-brand-orange">
              {userLabel.charAt(0).toUpperCase()}
            </div>
            <div className="text-xs leading-tight">
              <div className="text-white/90 font-medium truncate max-w-[150px]" data-testid="user-label">{userLabel}</div>
              <div className="text-white/40 text-[10px]">Connecté(e)</div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid="logout-btn"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
          >
            <LogOut size={16} className="mr-2" /> Se déconnecter
          </Button>
          <p className="small-caps text-[9px] text-white/40 mt-3 leading-relaxed">
            {ASSOC.address}
          </p>
        </div>
      </aside>

      {/* Topbar (mobile) */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-brand-border h-14 flex items-center px-4">
        <button
          data-testid="sidebar-open-btn"
          onClick={() => setOpen(true)}
          className="text-brand-navy"
        >
          <Menu size={22} />
        </button>
        <img src={ASSOC.logo} alt="AIJ" className="h-8 w-8 ml-3 object-contain" />
        <span className="ml-2 font-serif text-lg text-brand-navy">Action Impact Jeunesse</span>
      </div>

      {/* Main */}
      <main className="lg:pl-72">
        <div className="p-6 md:p-10 max-w-[1400px] mx-auto animate-fade-up">
          <Outlet />
        </div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
